import React, { FC, useMemo, useContext, useState, useCallback, useEffect } from 'react'
import { NavLinkProps, RouteComponentProps, withRouter, NavLink } from 'react-router-dom'
import { useHistory, useLocation } from 'react-router'
import {
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
  Box,
  Hidden,
  IconButton,
  Dialog,
  FormControl,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Button,
  InputLabel,
  OutlinedInput,
  InputAdornment
} from '@material-ui/core'
import { toast } from 'react-toastify'
import { handleError } from 'src/core'
import { formatServerError } from 'src/helpers'
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuList from '@material-ui/core/MenuList'
import { ThemeProvider, createStyles, makeStyles, useTheme } from '@material-ui/styles'
import { useUserInfo } from 'src/services'
import Tooltip from '@material-ui/core/Tooltip'
import { useQuery, useMutation, useSubscription } from 'react-apollo'
import * as ApolloAPI from 'src/apollo/api'
import { MainTheme } from 'src/theme'
import { COLOR_WHITE, WorkspaceRoles } from 'src/constants'
import {
  getCurrentWorkspaceFromLocalStorage
} from 'src/helpers/local-storage'
import AddOutlinedIcon from '@material-ui/icons/AddOutlined';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import DevicesOutlinedIcon from '@material-ui/icons/DevicesOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import KeyboardArrowRightOutlinedIcon from '@material-ui/icons/KeyboardArrowRightOutlined';
import KeyboardArrowDownOutlinedIcon from '@material-ui/icons/KeyboardArrowDownOutlined';
import LiveHelpOutlinedIcon from '@material-ui/icons/LiveHelpOutlined';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';

import { LayoutContext } from '../templates/default-layout'
import { isHideFeature } from '../../helpers/white-label-helper'
import { WhiteLabelFeaturesEnum } from '../../helpers/white-label.enum'
import { useAdOptInStatus } from 'src/hooks/useAdOptInStatus'
import { ANALYTIC_SUBSCRIPTION_STATE_CHANGED } from 'src/apollo/api/paywall/subscriptions';
import urlHelper from 'src/helpers/urlHelper';
import { useCurrentWorkspace } from 'src/hooks/useCurrentWorkspace';
import {
  WORKSPACE_ID_PARAM,
  CONDITIONAL_CONTENT_SUB_PATH,
  PLAYERLIST_SUB_PATH,
  MARKETING_SUB_PATH,
  DOWNLOAD_PLAYER_SUB_PATH,
  HELP_PAGE_SUB_PATH,
  PEOPLE_COUNTER_SUB_PATH,
  CAR_TRACKER_SUB_PATH,
  DEVICE_MANAGEMENT_SUB_PATH,
  BILLING_SUB_PATH,
  ACCOUNT_SUB_PATH,
  BACK_OFFICE_SUB_PATH,
  WORKSPACE_URL_PATTERN,
  PROOF_OF_PERFORMANCE_SUB_PATH,
  DISPLAY_SUB_PATH,
  SCHEDULE_SUB_PATH,
  MY_MEDIA_SUB_PATH,
  LED_STRIPS_SUB_PATH,
  CUSTOMER_DASHBOARD_SUB_PATH,
  AD_NETWORK_OVERVIEW_SUB_PATH,
  AD_NETWORK_PROFILE_SUB_PATH,
  AD_NETWORK_TERMS_SUB_PATH,
} from '../../constants/urlConstants';

const useStyles = makeStyles<Theme>(theme => {
  return createStyles({
    workspaceRow: {
      display: 'flex',
      justifyContent: 'space-between',
      height: theme.spacing(6),
      alignItems: 'center',
      borderStyle: 'solid',
      borderColor: '#696B6C',
      borderWidth: '1px',
      borderRadius: '4px',
      padding: '4',
      width: '95%'
    },
    workspaceTitle: {
      flexGrow: 2,
      display: 'flex',
      justifyContent: 'start',
      overflowX: 'hidden',
      textOverflow: 'ellipsis',
      marginLeft: '8px'
    },
    iconsWorkspace: {
      display: 'flex',
      color: '#41444E',
      marginLeft: 3,
      height: theme.spacing(4),
      width: theme.spacing(4)
    },
    passwordFormControl: {
      margin: theme.spacing(3),
      width: '90%'
    },
    activeMenu: {
      backgroundColor: '#1D2026',
      borderLeftWidth: '4px',
      borderLeftStyle: 'solid',
      borderLeftColor: '#1D93F3',
      borderTopLeftRadius: '4px',
      borderBottomLeftRadius: '4px',
    },
    menuList: {
      paddingTop: '4px',
      paddingBottom: '4px'
    },
    menuPaper: {
      backgroundColor: '#FFFFFF'
    },
  })
})

const hasWorkspacePath = (workspaceId) => {
  const pathPattern = WORKSPACE_URL_PATTERN.replace(WORKSPACE_ID_PARAM, encodeURIComponent(workspaceId));

  return window.location.pathname.includes(pathPattern);
}

const checkForbidChangingWorkspace = () => {
  const path = window.location.pathname

  // In the inside page of the path, probably it's the editor page
  const forbiddenPaths = [
    `${CONDITIONAL_CONTENT_SUB_PATH}/`,
    `${PLAYERLIST_SUB_PATH}/`,
    MARKETING_SUB_PATH,
    DOWNLOAD_PLAYER_SUB_PATH,
    HELP_PAGE_SUB_PATH,
    PEOPLE_COUNTER_SUB_PATH,
    CAR_TRACKER_SUB_PATH,
    DEVICE_MANAGEMENT_SUB_PATH,
    BILLING_SUB_PATH,
    ACCOUNT_SUB_PATH,
    BACK_OFFICE_SUB_PATH,
  ]

  return forbiddenPaths.some(pathToCheck => path.includes(pathToCheck))
}

const NavContentView: FC<RouteComponentProps<any>> = ({ location }) => {
  const ctx = useContext(LayoutContext)

  const { navVariant, collapsible, collapsed } = ctx

  const history = useHistory()

  const isMenuHidden = (collapsible && collapsed) || navVariant === 'persistent'

  // 0 for close, 1 for open in create mode, 2 for open in edit mode
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null)
  const [open, setOpen] = useState(0)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [isShowPassword, setIsShowPassword] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [hasAnalyticsSubscription, setHasAnalyticsSubscription] = useState(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)

  const {
    me: {
      role,
      hasPassword,
      account: { id: accountId }
    }
  } = useUserInfo()
  const currentWorkspaceData = useCurrentWorkspace();

  const {
    loading: getWorkspacesLoading,
    data: getWorkspacesData,
    refetch: getWorkspacesRefetch
  } = useQuery(ApolloAPI.SC_GET_WORKSPACES, {
    fetchPolicy: 'cache-and-network'
  })

  const [createWorkspace] = useMutation(ApolloAPI.SC_CREATE_WORKSPACE)
  const [updateWorkspace] = useMutation(ApolloAPI.SC_UPDATE_WORKSPACE)
  const [deleteWorkspace] = useMutation(ApolloAPI.SC_DELETE_WORKSPACE)

  let currentWorkspace = currentWorkspaceData?.data

  useQuery(ApolloAPI.GQL_GET_ANALYTICS_SUBSCRIPTION_OF_CURRENT_WORKSPACE, {
    variables: {
      workspaceId: currentWorkspace?.id
    },
    skip: !currentWorkspace?.id,
    onCompleted: ({ getLatestSubscription }) => {
      if (getLatestSubscription) {
        setHasAnalyticsSubscription(getLatestSubscription.status === 'ACTIVE')
      }
    }
  })

  useSubscription(ANALYTIC_SUBSCRIPTION_STATE_CHANGED, {
    skip: !accountId,
    variables: { accountId },
    fetchPolicy: 'no-cache',
    onSubscriptionData: ({
      subscriptionData: {
        data: { analyticSubscriptionStateChanged }
      }
    }) => {
      setHasAnalyticsSubscription(analyticSubscriptionStateChanged?.state === 'ACTIVE')
    }
  })

  if (!currentWorkspace) {
    let storedWorkspace = getCurrentWorkspaceFromLocalStorage()

    if (storedWorkspace && getWorkspacesData?.workspaces) {
      const found = getWorkspacesData.workspaces.find(w => w.id === storedWorkspace.id)

      if (!found) {
        storedWorkspace = null
      }
    }

    currentWorkspace = storedWorkspace || getWorkspacesData?.workspaces[0]
  }

  const adOptInStatus = useAdOptInStatus()

  const getAdNetworkBadge = () => {
    if (!adOptInStatus) return null

    const { hasOptedIn, profileCompleted, termsCompleted, agreementStatus } = adOptInStatus

    if (agreementStatus === 'active' && termsCompleted) {
      return (
        <Box style={{
          display: 'inline-flex',
          padding: '1px 6px',
          borderRadius: '7px',
          background: '#1B5E20',
          fontSize: '10px',
          color: '#FFFFFF',
          fontWeight: 500,
          lineHeight: '16px',
          whiteSpace: 'nowrap',
        }}>
          Active
        </Box>
      )
    }

    if (hasOptedIn || profileCompleted) {
      const steps = [hasOptedIn, profileCompleted, termsCompleted].filter(Boolean).length
      return (
        <Box style={{
          display: 'inline-flex',
          padding: '1px 6px',
          borderRadius: '7px',
          background: '#E65100',
          fontSize: '10px',
          color: '#FFFFFF',
          fontWeight: 500,
          lineHeight: '16px',
          whiteSpace: 'nowrap',
        }}>
          {steps}/3
        </Box>
      )
    }

    return null
  }

  const filteredMenuItems = useMemo(
    () => items =>
      items.filter(item => {
        const notAllowedByRole = item.allowedRoles && !item.allowedRoles.includes(role)
        if (notAllowedByRole) {
          return false
        }

        return true
      }),
    [role]
  )

  const validateName = useCallback(() => {
    return name !== ''
  }, [name])

  const cancelAddWorkspace = useCallback(() => {
    setName('')
    setOpen(0)
  }, [])

  const handleSubmit = useCallback(async () => {
    try {
      if (open === 1) {
        const { data } = await createWorkspace({
          variables: {
            name: name
          }
        })

        if (!data.createWorkspace.success) {
          return handleError({
            error: new Error(
              formatServerError({
                code: data.createWorkspace.code,
                message: data.createWorkspace.message
              })
            ),
            userFriendlyMessage: data.createWorkspace.message
          })
        }

        setName('');
        setOpen(0);

        await getWorkspacesRefetch();

        toast.success('Workspace was successfully created');

        const nextWorkspaceId = data?.createWorkspace?.workspace?.id;

        if (nextWorkspaceId) {
          const pathPattern = WORKSPACE_URL_PATTERN.replace(WORKSPACE_ID_PARAM, encodeURIComponent(currentWorkspace?.id));

          if (window.location.pathname.includes(pathPattern)) {
            const newPath = window.location.pathname.replace(
              encodeURIComponent(currentWorkspace?.id),
              encodeURIComponent(nextWorkspaceId),
            );

            window.location.href = newPath;
          }
        }

      } else {
        const { data } = await updateWorkspace({
          variables: {
            workspaceId: selectedWorkspaceId,
            name: name
          }
        })
        if (!data.updateWorkspace.success) {
          return handleError({
            error: new Error(
              formatServerError({
                code: data.updateWorkspace.code,
                message: data.updateWorkspace.message
              })
            ),
            userFriendlyMessage: data.updateWorkspace.message
          })
        }

        setName('');
        setOpen(0);

        return toast.success('Workspace was successfully updated');
      }
    } catch (error) {
      return handleError({
        error,
        userFriendlyMessage: 'Error occurred. Workspace was not created.'
      })
    }
  }, [
    createWorkspace,
    currentWorkspace,
    history,
    name,
    open,
    getWorkspacesRefetch,
    updateWorkspace
  ])

  const handleDeleteWorkspace = useCallback(async () => {
    try {
      const { data: deletedData } = await deleteWorkspace({
        variables: {
          workspaceId: selectedWorkspaceId,
          password
        }
      })
      if (!deletedData.deleteWorkspace.success) {
        return handleError({
          error: new Error(
            formatServerError({
              code: deletedData.deleteWorkspace.code,
              message: deletedData.deleteWorkspace.message
            })
          ),
          userFriendlyMessage: deletedData.deleteWorkspace.message
        })
      }
      setDeleteModalOpen(false)
      setPassword('')
      setSelectedWorkspaceId(null)
      setIsShowPassword(false)

      await getWorkspacesRefetch();

      return toast.success('Workspace was successfully deleted')
    } catch (error) {
      return handleError({
        error,
        userFriendlyMessage: 'Error occurred. Workspace was not deleted.'
      })
    }
  }, [
    currentWorkspace.id,
    getWorkspacesData.workspaces,
    deleteWorkspace,
    history,
    getWorkspacesRefetch,
    password
  ])

  const handleMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
    []
  )
  const handleCloseMenu = useCallback(() => setAnchorEl(null), [])

  const handleWorkspaceChange = async workspaceId => {

    const workspace = getWorkspacesData.workspaces.find(
      workspace => workspace.id === workspaceId
    );

    const nextWorkspaceId = workspace?.id;

    if (nextWorkspaceId) {
      const pathPattern = WORKSPACE_URL_PATTERN.replace(WORKSPACE_ID_PARAM, encodeURIComponent(currentWorkspace?.id));

      if (window.location.pathname.includes(pathPattern)) {
        const newPath = window.location.pathname.replace(
          encodeURIComponent(currentWorkspace?.id),
          encodeURIComponent(nextWorkspaceId),
        );

        window.location.href = newPath;
      }
    }
  }

  const theme = useTheme()
  const classes = useStyles(theme)
  const hasWorkspaceUrl = hasWorkspacePath(currentWorkspace?.id);
  const forbidChangingWorkspace = checkForbidChangingWorkspace();
  const isHideUserAdmin = isHideFeature(WhiteLabelFeaturesEnum.USER_ADMIN_PAGE)
  const isHideAnalytics = isHideFeature(WhiteLabelFeaturesEnum.ANALYTICS_PAGE)
  const isHideProduct = isHideFeature(WhiteLabelFeaturesEnum.PRODUCT_PAGE)
  const isHideLED = true; //isHideFeature(WhiteLabelFeaturesEnum.LED_STRIP_PAGE);

  const AdapterLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => (
    <NavLink innerRef={ref as any} {...props} />
  ))

  const NavigationItem = ({ icon, primaryText, to, isMenuHidden, statusIcon = null }) => {
    return (
      <Tooltip placement="right" title={!isMenuHidden ? '' : primaryText.toUpperCase()}>
        <ListItem activeClassName={classes.activeMenu} button component={AdapterLink} to={to}>
          <ListItemIcon style={{ width: '20px', minWidth: '20px' }}>{icon}</ListItemIcon>
          <ListItemText primary={primaryText} primaryTypographyProps={{ noWrap: true }} />
          {statusIcon && <Box style={{ marginLeft: 'auto', display: 'flex' }}>{statusIcon}</Box>}
        </ListItem>
      </Tooltip>
    )
  }

  const CollapseMenu = (props) => {
    const [openCollapse, setOpenCollapse] = useState(false)
    const location = useLocation()

    const isSubmenuActive = props.menu.some(
      (item) =>
        location.pathname ===
        `${WORKSPACE_URL_PATTERN.replace(
          WORKSPACE_ID_PARAM,
          encodeURIComponent(currentWorkspace?.id)
        )}${item.to}`
    );

    useEffect(() => {
      setOpenCollapse(isSubmenuActive);
    }, [isSubmenuActive]);

    const handleToggle = () => {
      if (!isSubmenuActive) {
        setOpenCollapse(!openCollapse);
      }
      // setOpenCollapse(!openCollapse);
    };


    return (
      <List style={{ paddingLeft: '16px', paddingRight: '12px' }}>
        <ListItem button onClick={handleToggle} style={{ padding: '4px' }}>
          {props.icon}
          <ListItemText primary={props.title} style={{ paddingLeft: '8px' }} />
          {props.badge && <Box style={{ marginRight: '4px' }}>{props.badge}</Box>}
          {openCollapse ? (
            <KeyboardArrowDownOutlinedIcon />
          ) : (
            <KeyboardArrowRightOutlinedIcon />
          )}
        </ListItem>


        {openCollapse && (
          <List>
            {filteredMenuItems(props.menu).map(({ primaryText, icon, to, hideItem, statusIcon }, key) => {
              if (hideItem) return null;
              return (
                <NavigationItem
                  icon={icon}
                  isMenuHidden={isMenuHidden}
                  key={key}
                  primaryText={primaryText}
                  statusIcon={statusIcon}
                  to={`${WORKSPACE_URL_PATTERN.replace(WORKSPACE_ID_PARAM, encodeURIComponent(currentWorkspace?.id))}${to}`}
                />
              );
            })}
          </List>
        )}
      </List>
    )
  }

  const devicesMenu = [
    {
      allowedRoles: null,
      primaryText: 'Displays',
      to: DISPLAY_SUB_PATH,
    },
    {
      allowedRoles: null,
      primaryText: 'Analytic Devices',
      to: DEVICE_MANAGEMENT_SUB_PATH,
      hideItem: isHideAnalytics
    },
    {
      allowedRoles: null,
      primaryText: 'LED Strips',
      to: LED_STRIPS_SUB_PATH,
      hideItem: isHideLED
    },
  ];

  const contentMenu = [
    {
      allowedRoles: null,
      primaryText: 'Playlists',
      to: PLAYERLIST_SUB_PATH,
    },
    {
      allowedRoles: null,
      primaryText: 'Schedule',
      to: SCHEDULE_SUB_PATH,
    },
    {
      allowedRoles: null,
      primaryText: 'My Media',
      to: MY_MEDIA_SUB_PATH,
    },
    {
      allowedRoles: null,
      primaryText: 'Conditional Content',
      to: CONDITIONAL_CONTENT_SUB_PATH,
      hideItem: isHideAnalytics || !hasAnalyticsSubscription
    }
  ];

  const reportMenu = [
    {
      allowedRoles: null,
      primaryText: 'Car Tracker',
      to: CAR_TRACKER_SUB_PATH,
      hideItem: isHideAnalytics
    },
    {
      allowedRoles: null,
      primaryText: 'People Counter',
      to: PEOPLE_COUNTER_SUB_PATH,
      hideItem: isHideAnalytics
    },
    {
      allowedRoles: null,
      primaryText: 'Proof of Performance',
      to: PROOF_OF_PERFORMANCE_SUB_PATH,
    },
  ];

  const originalServiceMenu = [
    {
      allowedRoles: null,
      primaryText: 'Downloads',
      to: DOWNLOAD_PLAYER_SUB_PATH,
    },
    {
      allowedRoles: null,
      primaryText: 'Help',
      to: HELP_PAGE_SUB_PATH,
    },
  ];

  const serviceMenu = originalServiceMenu.filter(item => {
    if (isHideFeature(WhiteLabelFeaturesEnum.HELP_SECTION) && item.to === HELP_PAGE_SUB_PATH) {
      return false
    }

    if (isHideFeature(WhiteLabelFeaturesEnum.DOWNLOADS) && item.to === DOWNLOAD_PLAYER_SUB_PATH) {
      return false
    }

    return true
  });

  const adNetworkMenu = [
    {
      allowedRoles: null,
      primaryText: 'Overview',
      to: AD_NETWORK_OVERVIEW_SUB_PATH,
      statusIcon: adOptInStatus?.hasOptedIn ? <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} /> : null,
    },
    ...(adOptInStatus?.hasOptedIn ? [
      {
        allowedRoles: null,
        primaryText: 'Profile',
        to: AD_NETWORK_PROFILE_SUB_PATH,
        statusIcon: adOptInStatus?.profileCompleted ? <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} /> : null,
      },
      {
        allowedRoles: null,
        primaryText: 'Terms',
        to: AD_NETWORK_TERMS_SUB_PATH,
        statusIcon: adOptInStatus?.termsCompleted ? <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} /> : null,
      },
    ] : []),
  ];

  const otherMenu = [
    {
      allowedRoles: null,
      primaryText: 'Dashboard',
      icon: <DashboardOutlinedIcon />,
      to: CUSTOMER_DASHBOARD_SUB_PATH,
    },
    {
      allowedRoles: null,
      primaryText: 'Products',
      icon: <LocalMallOutlinedIcon />,
      to: MARKETING_SUB_PATH,
    },
  ];

  const disableChangeWorkspace= !hasWorkspaceUrl || forbidChangingWorkspace;

  const renderWorkspace = () => {
    return (
      <>
        <Button
          disabled={disableChangeWorkspace}
          onClick={handleMenu}
          style={{ textTransform: 'none', width: '100%', padding: '0px 2px' }}
        >
          <Box className={classes.workspaceRow} style={{ paddingLeft: '8px' }}>
            <div className={classes.workspaceTitle}>
              <Typography noWrap>{currentWorkspace.name}</Typography>
            </div>
            {!disableChangeWorkspace && (
              <ExpandMoreIcon style={{ color: COLOR_WHITE, marginRight: '8px' }} />
            )}
          </Box>
        </Button>
        {!getWorkspacesLoading && !currentWorkspaceData?.loading && (
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            classes={{ paper: classes.menuPaper, list: classes.menuList }}
            id='workspace-menu-account'
            onClose={handleCloseMenu}
            open={openMenu}
            style={{ top: 110, zIndex: 1400 }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <MenuList style={{ width: '260px', maxHeight: '280px', overflowY: 'scroll', overflowX: 'hidden' }}>
              {getWorkspacesData?.workspaces?.map(workspace => {
                const isDisabledEdit = !hasWorkspacePath || workspace.memberRole.name !== WorkspaceRoles.OWNER
                const isDisabledDelete = isDisabledEdit || workspace.id === currentWorkspace.id

                return (
                  <MenuItem key={`workspace-menu-${workspace.id}`} value={workspace.id} onClick={async (e) => {
                    handleCloseMenu();
                    handleWorkspaceChange(workspace.id);
                  }}>
                    <CheckOutlinedIcon style={{ width: '20px', marginRight: '8px', color: '#41444E', visibility: workspace.id === currentWorkspace.id ? 'unset' : 'hidden' }} />
                    <ListItemText>
                      <Typography
                        style={{
                          color: '#1D2026',
                          fontSize: 14,
                          fontWeight: 400,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden'
                        }}
                      >
                        {workspace.name}
                      </Typography>
                    </ListItemText>
                    {isDisabledEdit && (
                      <EditOutlinedIcon
                        style={{ width: '20px', marginLeft: '16px' , color: '#00000042', cursor: 'default' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      />
                    )}

                    {!isDisabledEdit && (
                      <IconButton
                        className={classes.iconsWorkspace}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleCloseMenu();
                          setName(workspace.name);
                          setSelectedWorkspaceId(workspace.id);
                          setOpen(2);
                        }}>
                        <EditOutlinedIcon style={{ width: '20px', marginLeft: '16px' }} />
                      </IconButton>
                    )}
                    {isDisabledDelete && (
                      <DeleteOutlinedIcon
                        style={{ width: '20px', marginLeft: '16px' , color: '#00000042', cursor: 'default' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      />
                    )}
                    {!isDisabledDelete && (
                      <IconButton
                        className={classes.iconsWorkspace}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setSelectedWorkspaceId(workspace.id);
                          setDeleteModalOpen(true);
                        }}>
                        <DeleteOutlinedIcon style={{ width: '20px', marginLeft: '16px' }} />
                      </IconButton>
                    )}
                  </MenuItem>)
              })}
            </MenuList>
            <Divider variant="fullWidth" style={{ backgroundColor: '#DADADA' }} />
            <MenuItem onClick={() => {
              handleCloseMenu();
              setOpen(1);
            }}>
              <ListItem>
                <AddOutlinedIcon style={{ color: '#41444E', marginRight: '12px' }} />
                <Typography style={{ color: '#1D2026', fontSize: 16, fontWeight: 400 }}>{'Create Workspace'}</Typography>
              </ListItem>
            </MenuItem>
          </Menu>
        )}
      </>
    );
  };

  return (
    <List component="nav" style={{ maxHeight: '100%', overflow: 'auto', paddingBottom: '36px' }}>
      <ThemeProvider theme={MainTheme}>
        <Dialog
          aria-labelledby="select-workspace-name"
          onClose={cancelAddWorkspace}
          open={open !== 0}
        >
          <FormControl>
            <DialogTitle title="select-workspace-name">
              Choose a name for your Workspace
            </DialogTitle>
            <DialogContent>
              <div>
                <TextField
                  id="outlined-name"
                  label="Workspace Name"
                  margin="normal"
                  onChange={e => setName(e.target.value)}
                  value={name}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={cancelAddWorkspace} type="button">
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={!validateName()}
                onClick={handleSubmit}
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </FormControl>
        </Dialog>
        <Dialog aria-labelledby="delete-workspace" open={deleteModalOpen}>
          <FormControl>
            <DialogTitle title="delete-workspace">Delete Workspace</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                Deleting your workspace will permanently delete all displays, playlists and media
                associated with this workspace.
                <br />
                <br />
                {hasPassword
                  ? 'Please enter your password below if you wish to permanently delete yuour workspace.'
                  : 'Please type “permanently delete” below if you wish to permanently delete your workspace.'}
              </Typography>
            </DialogContent>
          </FormControl>
          <FormControl className={classes.passwordFormControl} fullWidth variant="outlined">
            <InputLabel htmlFor="standard-adornment-password">Type "permanently delete"</InputLabel>
            <OutlinedInput
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={() => setIsShowPassword(!isShowPassword)}
                    onMouseDown={() => {}}
                  />
                </InputAdornment>
              }
              id="outlined-adornment-password"
              labelWidth={hasPassword ? 75 : 190}
              onChange={e => setPassword(e.target.value)}
              type="text"
              value={password}
            />
          </FormControl>
          <FormControl>
            <DialogActions>
              <Button onClick={() => setDeleteModalOpen(false)} type="button">
                Cancel
              </Button>
              <Button
                color="secondary"
                data-test="delete button"
                disabled={password.length < 1}
                onClick={handleDeleteWorkspace}
              >
                Delete
              </Button>
            </DialogActions>
          </FormControl>
        </Dialog>
      </ThemeProvider>

      {!isMenuHidden && (
        <Hidden only="sm">
          {renderWorkspace()}
        </Hidden>
      )}

      <CollapseMenu title={'Devices'} menu={devicesMenu} icon={<DevicesOutlinedIcon />} />
      <CollapseMenu title={'Content Management'} menu={contentMenu} icon={<ImageOutlinedIcon />} />
      <CollapseMenu title={'Reports'} menu={reportMenu} icon={<AssessmentOutlinedIcon />} />

      <CollapseMenu title={'Ad Network'} menu={adNetworkMenu} icon={<LocalMallOutlinedIcon />} badge={getAdNetworkBadge()} />

      <Tooltip placement="right" title={!isMenuHidden ? '' : otherMenu[0].primaryText.toUpperCase()}>
        <List style={{ paddingLeft: '12px' }}>
          <ListItem
            activeClassName="Mui-selected"
            component={AdapterLink}
            to={`${WORKSPACE_URL_PATTERN.replace(WORKSPACE_ID_PARAM, encodeURIComponent(currentWorkspace?.id))}${otherMenu[0].to}`}
            button
            style={{ padding: '4px' }}>
            {otherMenu[0].icon}
            <Hidden only="sm">
              <ListItemText primary={otherMenu[0].primaryText} style={{ paddingLeft: '8px' }} />
            </Hidden>
          </ListItem>
        </List>
      </Tooltip>

      {serviceMenu && serviceMenu.length > 0 && (
        <CollapseMenu title={'Services'} menu={serviceMenu} icon={<LiveHelpOutlinedIcon />} />
      )}

      {!isHideProduct && (
        <Tooltip placement="right" title={!isMenuHidden ? '' : otherMenu[1].primaryText.toUpperCase()}>
          <List style={{ paddingLeft: '12px' }}>
            <ListItem
              activeClassName="Mui-selected"
              component={AdapterLink}
              to={`${WORKSPACE_URL_PATTERN.replace(WORKSPACE_ID_PARAM, encodeURIComponent(currentWorkspace?.id))}${otherMenu[1].to}`}
              button
              style={{ padding: '4px' }}>
              {otherMenu[1].icon}
              <Hidden only="sm">
                <ListItemText primary={otherMenu[1].primaryText} style={{ paddingLeft: '8px' }} />
              </Hidden>
            </ListItem>
          </List>
        </Tooltip>
      )}

      {role === 'admin' && !isHideUserAdmin && (
        <Tooltip placement="right" title={!isMenuHidden ? '' : 'USERS'}>
          <List style={{ paddingLeft: '12px' }}>
            <ListItem
              activeClassName="Mui-selected"
              component={AdapterLink}
              to={urlHelper.getBackOfficePageUrl(currentWorkspace?.id)}
              button
              style={{ padding: '4px' }}>
              <PeopleOutlinedIcon />
              <Hidden only="sm">
                <ListItemText primary={'Users'} style={{ paddingLeft: '8px' }} />
              </Hidden>
            </ListItem>
          </List>
        </Tooltip>
      )}
    </List>
  )
}

export const NavContent = withRouter(NavContentView)
